import React, { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { useDeleteFeature, useFetchLayer } from 'services/layer'
import { Alert } from 'components/Alert'
import { Button } from 'components/Button'
import { Dialog } from 'components/Dialog'
import { Flex } from 'components/Flex'
import { Heading } from 'components/Heading'
import { Stack } from 'components/Stack'
import { Text } from 'components/Text'
import { fire } from 'utils/customEvent'
import { RefreshLayer, Events } from 'constants/events'

type DeleteFeatureDialogProps = {
  layerId: number
  infowindowId: number
  close: () => void
  cancel: () => void
}

export const DeleteFeatureDialog: FC<DeleteFeatureDialogProps> = ({
  layerId,
  infowindowId,
  close,
  cancel,
}) => {
  const { t } = useTranslation()
  const { layer } = useFetchLayer(layerId)
  const [deleteFeature, { status }] = useDeleteFeature(layerId, infowindowId)

  const handleConfirmation = () => {
    deleteFeature(undefined, {
      onSuccess: () => {
        close()
        fire<RefreshLayer>(Events.RefreshLayer, { sourceLayer: layer.viz.source! })
      },
    })
  }

  return (
    <Dialog aria-label={t(`infowindow_delete.title`)} isOpen={true} onClose={close}>
      <Stack>
        <header>
          <Flex justify="start" align="center">
            <Heading size="card">{t(`infowindow_delete.title`)}</Heading>
          </Flex>
          <Text size="small" component="span">
            {t(`infowindow_delete.desc`)}
          </Text>
        </header>

        <Stack space="small">
          <Button full variant="secondary" onClick={cancel}>
            {t(`infowindow_delete.cancel`)}
          </Button>
          <Button full variant="danger" onClick={handleConfirmation} loading={status === 'loading'}>
            {t(`infowindow_delete.cta`)}
          </Button>
          {status === 'error' && (
            <Alert full tone="critical">
              {t('common.messages.error')}
            </Alert>
          )}
        </Stack>
      </Stack>
    </Dialog>
  )
}

DeleteFeatureDialog.displayName = 'DeleteFeatureDialog'
